import i18n from "@/lib/i18n/i18n"
import { createSelector } from "@reduxjs/toolkit"
import {
  selectAccountChatByJid,
  selectAccountChats,
  selectAccountChatsAsList,
} from "@/reducers/accountChatsSlice"
import { AccountChat } from "@/lib/accountChatsParser"
import { findCurrentTarget, Route2g, RouteStop } from "./routes2gSlice"
import { RootState } from "./store"
import { DriverLocation, Location } from "@/reducers/locationsSlice"
import { Profile } from "./profilesSlice"
import { findProfile } from "./profilesSliceSelectors"
import { Activity, selectActivities } from "@/reducers/activitySlice"
import {
  ReferenceRouteStatus,
  selectReferenceRouteStatuses,
} from "./referenceRouteStatusesSlice"
import { Chat } from "@/reducers/chatsSlice"
import ChatHelpers from "@/lib/chatHelpers"
import {
  selectChatCountsForCurrentUser,
  selectChats,
} from "@/reducers/chatsSliceSelectors"
import { Car } from "@/reducers/carsSlice"
import { driverLocal } from "@/jidUtils"
import { resolveDriverJidForChat } from "@/lib/resolveDriverForChat"
import {
  getLocationJidFromChatJid,
  getRouteJidFromChatJid,
} from "@/reducers/selectors"
import { isDriverRoomGroup } from "@/lib/slickAISilence"
import {
  carLabelWithoutPlate,
  chatNamePrefix,
  normalizePlate,
} from "@/lib/plates"
import { jidIdentity } from "@/jidUtils"
import type { ChatAutomationScope } from "@/lib/chatAutomations"

// Helper function to get the part of the JID before the "@"
const getJidPrefix = (jid: string): string => {
  const atIndex = jid.indexOf("@")
  return atIndex !== -1 ? jid.substring(0, atIndex) : jid
}

// Helper function to transform JID: remove the first segment after "@"
// e.g., "user@segment1.segment2.domain" becomes "user@segment2.domain"
// e.g., "user@segment1.domain" becomes "user@domain"
// e.g., "user@domain" remains "user@domain"
const transformJid = (jid: string): string => {
  const atIndex = jid.indexOf("@")
  if (atIndex === -1) {
    return jid // No "@" symbol, return as is
  }

  const username = jid.substring(0, atIndex)
  const domainFull = jid.substring(atIndex + 1)
  const domainParts = domainFull.split(".")

  if (domainParts.length > 1) {
    // If there's more than one segment in the domain part (e.g., conference.beedee.eu or kaem.com)
    // remove the first segment and join the rest.
    const newDomain = domainParts.slice(1).join(".")
    return `${username}@${newDomain}`
  }
  // If there's only one segment (e.g., localhost) or it's empty, return with the original domain part.
  return `${username}@${domainFull}`
}

// Selector for chat JIDs from the chats slice
const selectChatJids = createSelector(
  (state: RootState) => state.chats,
  (chatsArray) => new Set(chatsArray.map((chat) => chat.jid)), // Map over the array to get JIDs
)

// Helper function to process driver account chats with deduplication logic
// Returns a map of transformed JIDs to values produced by the mapper function
//
// Every group `isDriverRoomGroup` names is a room a vehicle publishes its
// location under, so every one of them belongs here. Listing "driver" and
// "shared_driver" by hand used to blank the whole Fleet Overview for an account
// whose vehicles are all `transics_driver` rooms.
const processDriverAccountChats = <T>(
  accountChatsList: AccountChat[],
  mapper: (chat: AccountChat) => T,
): Map<string, T> => {
  const sharedDriversMap = new Map<string, AccountChat>()
  const otherDriverRoomsMap = new Map<string, AccountChat>()

  // 1. Bucket the driver rooms by JID prefix. shared_driver keeps its own
  //    bucket because it outranks a same-prefix room of any other group.
  for (const chat of accountChatsList) {
    if (chat.hidden) continue
    if (!isDriverRoomGroup(chat.group)) continue
    const prefix = getJidPrefix(chat.jid)
    if (chat.group === "shared_driver") {
      sharedDriversMap.set(prefix, chat)
    } else {
      otherDriverRoomsMap.set(prefix, chat)
    }
  }

  const finalMap = new Map<string, T>()

  // 2. Process shared_drivers first and add with their transformed JIDs
  //    This ensures shared_drivers take precedence.
  for (const accountChat of sharedDriversMap.values()) {
    finalMap.set(transformJid(accountChat.jid), mapper(accountChat))
  }

  // 3. Process the rest: add their transformed JIDs only if no shared_driver
  //    has the same prefix
  for (const [prefix, accountChat] of otherDriverRoomsMap.entries()) {
    if (!sharedDriversMap.has(prefix)) {
      finalMap.set(transformJid(accountChat.jid), mapper(accountChat))
    }
  }

  return finalMap
}

// Selector for all driver JIDs (not filtered by user chats)
export const selectAllDriversJids = createSelector(
  [selectAccountChatsAsList],
  (accountChatsList: AccountChat[]): Map<string, string> => {
    return processDriverAccountChats(accountChatsList, (chat) => chat.jid)
  },
)

// Selector for all driver AccountChats mapped by transformed JIDs
export const selectAllDriversAccountChats = createSelector(
  [selectAccountChatsAsList],
  (accountChatsList: AccountChat[]): Map<string, AccountChat> => {
    return processDriverAccountChats(accountChatsList, (chat) => chat)
  },
)

// Selector for driver JIDs filtered by user chats (existing functionality)
export const selectDriversJids = createSelector(
  [selectAccountChatsAsList, selectChatJids], // Add selectChatJids as an input
  (
    accountChatsList: AccountChat[],
    chatJids: Set<string>,
  ): Map<string, string> => {
    // Filter accountChatsList to include only those present in chats
    const filteredAccountChatsList = accountChatsList.filter((accountChat) =>
      chatJids.has(accountChat.jid),
    )

    return processDriverAccountChats(
      filteredAccountChatsList,
      (chat) => chat.jid,
    )
  },
)

export const createSelectChatJidByDriverJid = (driverJid: string) =>
  createSelector(
    [selectAccountChatsAsList, selectChats],
    (
      accountChatsList: AccountChat[],
      chats: Chat[],
    ): { chatJid: string; shouldJoin: boolean } | undefined => {
      // First check if there's a chat for the driver

      const chat = chats?.find((chat) =>
        chat.participants.find(
          (p) => p.role === "driver" && p.jid === driverJid,
        ),
      )

      if (chat)
        return {
          chatJid: chat.jid,
          shouldJoin: false,
        }

      // If not, check if there's an account chat for the driver.
      // Use resolveDriverJidForChat which prefers driverA over stale MUC JID.
      const accountChat = accountChatsList?.find(
        (accountChat) =>
          (accountChat.groupName === "Drivers" ||
            accountChat.groupName === "Shared Drivers") &&
          resolveDriverJidForChat(accountChat, accountChat.jid) === driverJid,
      )

      if (accountChat) {
        return {
          chatJid: accountChat.jid,
          shouldJoin: true,
        }
      }
      return
    },
  )

export type ComplementaryDriver = {
  chatJid: string
  chatName: string
  driverJid: string | undefined
}

export const createSelectComplementaryDrivers = (
  chatJid: string,
  isSharedDriverChat: boolean,
) =>
  createSelector(
    [selectAccountChatsAsList],
    (accountChatsList: AccountChat[]): ComplementaryDriver[] => {
      const groupName = isSharedDriverChat ? "Drivers" : "Shared Drivers"

      // The pair is matched on the room's local part, which both sides of a
      // shared room keep, but the driver comes from the crew config - a room
      // named after whoever drove first says nothing about who drives now.

      return accountChatsList
        ?.filter(
          (accountChat) =>
            accountChat.groupName === groupName &&
            driverLocal(accountChat.jid) === driverLocal(chatJid),
        )
        .map((accountChat) => ({
          // The room found here *is* the answer, so hand it to the caller.
          // Returning a driver alone made the caller look the room up again,
          // and that round trip does not come back: the lookup takes the
          // first room the driver sits in, so a driver on two rooms sends the
          // click to whichever one happens to come first.
          chatJid: accountChat.jid,
          chatName: accountChat.name,
          // Label only. A crew room between drivers has none, and it is still
          // worth offering - dropping it left the office with no way across.
          driverJid: resolveDriverJidForChat(accountChat, accountChat.jid),
        }))
    },
  )

export type DriverDisplayInfo = {
  jid: string
  location: Location
  locationJid?: string
  stops?: RouteStop[]
  profile?: Profile
  isLate: boolean
  isNextStopLate: boolean
  isDriving: boolean
  isOffRoute: boolean
  unreadCount: number
  name: string
  avatarName: string
  avatarPhoto?: string
  avatarColor: string
  plates?: string
  // The configured car's own name ("47 SPRINTER", or "2 CW4129C" from the
  // chat name), for fleets that know their vehicles by side number rather
  // than plate. Only from the chat's car — a vCard has no such name.
  carLabel?: string
  isTracked: boolean
  showAlert: boolean
  hasRoute: boolean
  distance?: string
  nextStop?: RouteStop
  routeJid?: string
  chatJid?: string
  shouldJoin?: boolean
}

export type DriverInfoMap = Record<string, DriverDisplayInfo>

const selectLocations = (state: RootState) => state.locations
const selectRoutes2gData = (state: RootState) => state.routes2g.routes
const selectProfiles = (state: RootState) => state.profiles // Selector for profiles
const selectCars = (state: RootState) => state.cars

// Plates of every car some visible chat claims through carId. A vCard's
// plates pointing at one of these describe a car that config says belongs to
// another chat, so they must not be shown as a fallback.
export const getPlatesClaimedByChats = (
  accountChats: Record<string, AccountChat>,
  cars: Record<string, Car>,
): Set<string> => {
  const claimed = new Set<string>()
  for (const chat of Object.values(accountChats)) {
    if (chat.hidden || !chat.carId) continue
    const plates = cars[chat.carId]?.plates
    if (plates) claimed.add(normalizePlate(plates))
  }
  return claimed
}

/**
 * The plate to show for a driver, or undefined when none is trustworthy.
 *
 * Exported because the fuel report needs the same answer the driver cards
 * show — in particular the claimed-plate rule below, which is the whole
 * reason this cannot be a one-line `cars[carId].plates` lookup.
 */
export const resolveDriverPlate = ({
  accountChat,
  cars,
  driverProfile,
  platesClaimedByChats,
}: {
  accountChat: AccountChat | undefined
  cars: Record<string, Car>
  driverProfile: Profile | undefined
  platesClaimedByChats: Set<string>
}): string | undefined => {
  const carPlates = accountChat?.carId
    ? cars[accountChat.carId]?.plates
    : undefined
  if (carPlates) return carPlates

  // Not every chat has carId set, so the vCard's plates stay as a fallback —
  // but they are a snapshot from whenever the card was written, so when they
  // name a car that config assigns to a different chat, the snapshot is stale
  // and showing it would put one car on two chats.
  const profilePlates = driverProfile?.plates
  if (!profilePlates || profilePlates === "-") return undefined
  if (platesClaimedByChats.has(normalizePlate(profilePlates))) return undefined
  return profilePlates
}

/**
 * The fleet's own name for the chat's car: its label minus the plate, or,
 * when the label is only the plate, the side number the chat name carries in
 * front of it ("2 CW4129C/CW93989 Jacek D." -> "2 CW4129C"). euro-transchem's
 * GPS names every car by its plate, so the side number lives only in the chat
 * name (2026-11-03).
 */
export const resolveDriverCarLabel = (
  accountChat: AccountChat | undefined,
  cars: Record<string, Car>,
): string | undefined => {
  const car = accountChat?.carId ? cars[accountChat.carId] : undefined
  if (!car) return undefined
  const label = carLabelWithoutPlate(car.label, car.plates)
  if (label && normalizePlate(label) !== normalizePlate(car.plates)) {
    return label
  }
  const sideNumber = chatNamePrefix(accountChat?.name ?? "", car.plates)
  return sideNumber ? `${sideNumber} ${car.plates}` : undefined
}

/**
 * Who and what a driver room stands for, in the form automations are matched
 * against: the room, its current driver, and the plate of the truck the config
 * assigns to it. The driver comes from `driverA` and the plate from the car,
 * not from the room's vCard — a stale card names the previous driver or
 * another chat's truck, and either would pull a stranger's rules into this
 * room.
 */
export const selectChatAutomationScope = (chatJid: string) =>
  createSelector(
    [selectAccountChats, selectCars, selectProfiles],
    (accountChats, cars, profiles): ChatAutomationScope => {
      const accountChat = accountChats[chatJid]
      const driverJid = resolveDriverJidForChat(accountChat, chatJid)
      const plate = resolveDriverPlate({
        accountChat,
        cars,
        driverProfile: profiles.find((p) => p.jid === driverJid),
        platesClaimedByChats: getPlatesClaimedByChats(accountChats, cars),
      })

      return {
        jids: [chatJid, driverJid]
          .filter((jid): jid is string => Boolean(jid))
          .map(jidIdentity),
        plates: plate ? [normalizePlate(plate)] : [],
      }
    },
  )

// Helper function to create driver info map from JIDs
const createDriverInfoMap = (
  driverJidsMap: Map<string, string>,
  locations: Record<string, DriverLocation>,
  routes2g: Route2g[],
  profiles: Profile[],
  activities: Record<string, Activity>,
  referenceRouteStatuses: Record<string, ReferenceRouteStatus>,
  chatsArray: Chat[],
  accountChats: Record<string, AccountChat>,
  cars: Record<string, Car>,
  chatCounts: Record<string, number>,
): DriverInfoMap => {
  const driverJids = Array.from(driverJidsMap.keys())
  const resultMap: DriverInfoMap = {}
  const platesClaimedByChats = getPlatesClaimedByChats(accountChats, cars)
  // Create chat JID lookup for drivers
  const chatJidByDriverJid = new Map<string, string>()
  for (const chat of chatsArray) {
    // Find corresponding account chat to get driver JID
    const accountChat = accountChats[chat.jid]
    if (accountChat && isDriverRoomGroup(accountChat.group)) {
      const transformedJid = transformJid(accountChat.jid)
      chatJidByDriverJid.set(transformedJid, chat.jid)
    }
  }

  for (const driverJid of driverJids) {
    // Route/location keys must match the chat-based selectors used by the chat view.
    const accountChatJid = driverJidsMap.get(driverJid)
    const chatJid = chatJidByDriverJid.get(driverJid) || accountChatJid
    const locationJid = getLocationJidFromChatJid(chatJid, accountChats)
    const routeJid = getRouteJidFromChatJid(chatJid, accountChats)
    const accountChat =
      (chatJid ? accountChats[chatJid] : undefined) ||
      (accountChatJid ? accountChats[accountChatJid] : undefined)

    // 1. Get driver's current GPS location from locationsSlice
    const currentDriverGpsLocation = locationJid
      ? locations[locationJid]?.location
      : undefined

    // 2. Get the driver's route and all stops from routes2gSlice
    const route = routeJid
      ? routes2g.find((r) => r.jid === routeJid)
      : undefined
    const allStopsForRoute = route?.stops
    const driverProfile = profiles.find((p) => p.jid === driverJid)

    const isOffRoute = routeJid
      ? (referenceRouteStatuses[routeJid]?.isOffRoute ?? false)
      : false
    const isDriving = routeJid
      ? activities[routeJid]?.specific === "driving"
      : false

    const chat = chatsArray.find((c) => c.jid === chatJid)

    // Who drives now comes from the account chat config (driverA), never from
    // the room JID's vCard — that card can still belong to the previous driver
    // after a crew reassignment, and an empty driverA means the seat is empty,
    // not that the old card should be shown.
    const resolvedDriverJid = accountChat
      ? resolveDriverJidForChat(accountChat, accountChat.jid)
      : driverJid
    const effectiveDriverProfile = resolvedDriverJid
      ? profiles.find((p) => p.jid === resolvedDriverJid)
      : undefined

    // Display the account chat name — the same label the chat list shows — so
    // the card follows the backend's crew config instead of vCard contents.
    const name = accountChat?.name || effectiveDriverProfile?.name || driverJid
    const avatarPhoto = effectiveDriverProfile?.photo
      ? `data:image/jpeg;base64,${effectiveDriverProfile.photo}`
      : undefined
    const avatarName = effectiveDriverProfile
      ? `${effectiveDriverProfile.firstName} ${effectiveDriverProfile.lastName}`
      : name

    // Avatar color logic
    const chatColor = chat ? ChatHelpers.getChatColor(chat) : undefined
    const avatarColor = chatColor ?? "var(--defaultAvatarColor)"

    // Route and navigation data
    const shouldAlert = (stop: RouteStop) => {
      const eta =
        stop.estimated_arrival_time_with_tacho || stop.estimated_arrival_time
      if (!stop.scheduled_arrival_time || !eta) return false
      return new Date(eta) > new Date(stop.scheduled_arrival_time)
    }

    const formatStopDistance = (stop: RouteStop): string => {
      if (stop.proximity_status === "arrived")
        return i18n.t("app.driverInfo.arrived")
      if (stop.proximity_status === "arriving")
        return i18n.t("app.driverInfo.arriving")
      if (stop.remaining_distance && stop.remaining_distance >= 1000) {
        return `${Math.floor(stop.remaining_distance / 1000).toString()} km`
      } else if (stop.remaining_distance) {
        return `${stop.remaining_distance} m`
      }
      return ""
    }

    const displayedStop = findCurrentTarget(allStopsForRoute)
    const distance = displayedStop
      ? formatStopDistance(displayedStop)
      : undefined
    const isLate =
      allStopsForRoute?.some((stop: RouteStop) => shouldAlert(stop)) ?? false
    const isNextStopLate = displayedStop ? shouldAlert(displayedStop) : false

    // Route status flags
    const hasRoute = !!displayedStop
    const showAlert = !!displayedStop?.scheduled_arrival_time
    const isTracked = !!(routeJid && referenceRouteStatuses[routeJid])

    // Unread count
    const unreadCount = chatJid ? (chatCounts[chatJid] ?? 0) : 0

    if (currentDriverGpsLocation) {
      resultMap[driverJid] = {
        jid: driverJid,
        location: currentDriverGpsLocation,
        locationJid,
        stops: allStopsForRoute,
        profile: effectiveDriverProfile,
        isDriving,
        isOffRoute,
        unreadCount,
        name,
        avatarName,
        avatarPhoto,
        avatarColor,
        plates: resolveDriverPlate({
          accountChat,
          cars,
          driverProfile,
          platesClaimedByChats,
        }),
        carLabel: resolveDriverCarLabel(accountChat, cars),
        isNextStopLate,
        isLate,
        isTracked,
        showAlert,
        hasRoute,
        distance,
        nextStop: displayedStop,
        routeJid,
        chatJid,
        shouldJoin: !chat,
      }
    }
  }
  return resultMap
}

// Additional selectors needed for enhanced driver info
const selectChatsArray = (state: RootState) => state.chats
const selectAccountChatsMap = selectAccountChats

export const selectAccountChat = (jid: string) => selectAccountChatByJid(jid)

export type TranslationPair = { source: string; target: string }

/** Anyone with no preferred language recorded is treated as writing Polish. */
const languageOrDefault = (code: string | undefined) =>
  (code || "pl").toLowerCase()

/**
 * Which language an outgoing message to this chat is translated from and
 * into, or undefined when the dispatcher and the driver speak the same
 * language and the message should go out exactly as typed.
 *
 * Both sides come from the same `preferredLanguage` field on the account
 * node: mine from my own record, the driver's from driver A's (driver A is
 * the one every other feature keys off, see ShareRoute). Codes are compared
 * lowercased because the server validates only that a language is present.
 */
export const selectTranslationPair =
  (chatJid: string, myJid: string | undefined) =>
  (state: RootState): TranslationPair | undefined => {
    const driverJid = selectAccountChatByJid(chatJid)(state)?.driverA
    if (!driverJid) return undefined

    const source = languageOrDefault(
      findProfile(myJid)(state)?.preferredLanguage,
    )
    const target = languageOrDefault(
      findProfile(driverJid)(state)?.preferredLanguage,
    )
    if (source === target) return undefined

    return { source, target }
  }

// Selector for all drivers (not filtered by user chats)
export const selectAllDriverRouteAndLocationDetailsMap = createSelector(
  [
    selectAllDriversJids,
    selectLocations,
    selectRoutes2gData,
    selectProfiles,
    selectActivities,
    selectReferenceRouteStatuses,
    selectChatsArray,
    selectAccountChatsMap,
    selectCars,
    selectChatCountsForCurrentUser,
  ],
  (
    driverJids,
    locations,
    routes2g,
    profiles,
    activities,
    referenceRouteStatuses,
    chatsArray,
    accountChats,
    cars,
    chatCounts,
  ) =>
    createDriverInfoMap(
      driverJids,
      locations,
      routes2g,
      profiles,
      activities,
      referenceRouteStatuses,
      chatsArray,
      accountChats,
      cars,
      chatCounts,
    ),
)

// Selector for drivers filtered by user chats (existing functionality)
export const selectDriverRouteAndLocationDetailsMap = createSelector(
  [
    selectDriversJids,
    selectLocations,
    selectRoutes2gData,
    selectProfiles,
    selectActivities,
    selectReferenceRouteStatuses,
    selectChatsArray,
    selectAccountChatsMap,
    selectCars,
    selectChatCountsForCurrentUser,
  ],
  (
    driverJids,
    locations,
    routes2g,
    profiles,
    activities,
    referenceRouteStatuses,
    chatsArray,
    accountChats,
    cars,
    chatCounts,
  ) =>
    createDriverInfoMap(
      driverJids,
      locations,
      routes2g,
      profiles,
      activities,
      referenceRouteStatuses,
      chatsArray,
      accountChats,
      cars,
      chatCounts,
    ),
)
